import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  MessageCircle, Send, Search, Clock, Users,
  Plus, X, ChevronDown, Smile, Check
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  chatAPI,
  Conversation,
  Message,
  ProfessionalInDirectory,
  PROFESSIONAL_TYPES,
  PROVINCES,
} from '../services/chatService';
import { useSocket } from '../hooks/useSocket';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';
import { EmojiPicker } from '../components/EmojiPicker';
import BelagomProfilePhoto from '../components/BelagomProfilePhoto';
import { useTranslation } from 'react-i18next';
import { BelKnop } from '../components/BelKnop';

// ─── Sub-components ──────────────────────────────────────────────────────────

interface AvatarProps {
  photoPath?: string | null;
  firstName?: string;
  lastName?: string;
  size?: 'sm' | 'md';
  isOnline?: boolean;
}

const Avatar: React.FC<AvatarProps> = ({ photoPath, firstName, lastName, size = 'md', isOnline }) => {
  const px = size === 'sm' ? 32 : 40;
  return (
    <BelagomProfilePhoto
      src={photoPath}
      alt={firstName && lastName ? `${firstName} ${lastName}` : undefined}
      size={px}
      isOnline={isOnline}
    />
  );
};

// ─── Group creation modal ─────────────────────────────────────────────────────

interface GroupModalProps {
  onClose: () => void;
  onCreated: (conv: Conversation) => void;
  currentUserId: string;
}

const CreateGroupModal: React.FC<GroupModalProps> = ({ onClose, onCreated, currentUserId: _currentUserId }) => {
  const { t } = useTranslation('professional');
  const [groupName, setGroupName] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ProfessionalInDirectory[]>([]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['professionals-directory-modal', search],
    queryFn: () => chatAPI.searchProfessionals({ name: search || undefined }),
    enabled: true,
    staleTime: 10_000,
  });

  const createMutation = useMutation({
    mutationFn: () => chatAPI.createGroupConversation(groupName.trim(), selected.map(s => s.id)),
    onSuccess: (conv) => {
      toast.success(t('chat.groupCreated'));
      onCreated(conv);
    },
    onError: () => toast.error(t('chat.groupCreateError')),
  });

  const toggle = (p: ProfessionalInDirectory) => {
    setSelected(prev =>
      prev.find(s => s.id === p.id) ? prev.filter(s => s.id !== p.id) : [...prev, p]
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('chat.newGroup')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <input
            type="text"
            placeholder={t('chat.groupNamePlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={groupName}
            onChange={e => setGroupName(e.target.value)}
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('chat.searchProfessionalPlaceholder')}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Selected chips */}
        {selected.length > 0 && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {selected.map(p => (
              <span key={p.id} className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                {p.first_name} {p.last_name}
                <button onClick={() => toggle(p)} className="hover:text-blue-600">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto divide-y border-t">
          {isFetching ? (
            <div className="flex justify-center p-6">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
            </div>
          ) : results.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-8">{t('chat.noProfessionalsFound')}</p>
          ) : (
            results.map(p => {
              const isSelected = !!selected.find(s => s.id === p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggle(p)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                >
                  <Avatar photoPath={p.profile_photo_path} firstName={p.first_name} lastName={p.last_name} size="sm" isOnline={p.is_online} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{p.first_name} {p.last_name}</p>
                    <p className="text-xs text-gray-500">{p.professional_type_label}</p>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        <div className="p-4 border-t">
          <button
            onClick={() => createMutation.mutate()}
            disabled={!groupName.trim() || selected.length === 0 || createMutation.isPending}
            className="w-full py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createMutation.isPending ? t('chat.creating') : t('chat.createGroupWithCount', { count: selected.length })}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

type TabId = 'directory' | 'chats';

const ProfessionalChatPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<TabId>('directory');
  const [selectedConversation, setSelectedConversation] = useState<string | null>(
    searchParams.get('conversation') || null
  );
  const [newMessage, setNewMessage] = useState('');
  const [chatSearch, setChatSearch] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);

  // Directory filters
  const [dirSearch, setDirSearch] = useState('');
  const [filterProvince, setFilterProvince] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showProvinceFilter, setShowProvinceFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync URL param
  useEffect(() => {
    const id = searchParams.get('conversation');
    if (id) setSelectedConversation(id);
  }, [searchParams]);

  useEffect(() => {
    const current = searchParams.get('conversation');
    if (selectedConversation) {
      if (current !== selectedConversation) setSearchParams({ conversation: selectedConversation }, { replace: true });
    } else if (current) {
      setSearchParams({}, { replace: true });
    }
    // Alleen state → URL. Met searchParams als dep zou deze effect een URL-wijziging
    // van buitenaf terugdraaien voordat de effect hierboven de state heeft bijgewerkt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: professionals = [], isFetching: dirLoading } = useQuery({
    queryKey: ['professionals-directory', dirSearch, filterProvince, filterType],
    queryFn: () => chatAPI.searchProfessionals({
      name: dirSearch || undefined,
      province: filterProvince || undefined,
      professional_type: filterType || undefined,
    }),
    staleTime: 30_000,
  });

  const { data: conversations = [], isLoading: convsLoading } = useQuery({
    queryKey: ['conversations'],
    queryFn: chatAPI.getConversations,
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const { data: messages = [], isLoading: msgsLoading } = useQuery({
    queryKey: ['messages', selectedConversation],
    queryFn: () => selectedConversation ? chatAPI.getMessages(selectedConversation) : [],
    enabled: !!selectedConversation,
  });

  // ── Socket ───────────────────────────────────────────────────────────────

  const handleNewMessage = useCallback((msg: Message) => {
    if (String(msg.conversation_id) === String(selectedConversation)) {
      queryClient.setQueryData(['messages', selectedConversation], (old: Message[] = []) =>
        old.some(m => m.id === msg.id) ? old : [...old, msg]
      );
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }, [selectedConversation, queryClient]);

  const handleNotification = useCallback((n: any) => {
    if (n.type === 'new_message' && n.conversation_id !== selectedConversation) {
      toast.success(t('chat.newMessageReceived'));
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    }
  }, [selectedConversation, queryClient, t]);

  const { sendMessage: sendViaSocket, startTyping, stopTyping, markAsRead, typingUsers, isConnected: socketOk } = useSocket({
    conversationId: selectedConversation || undefined,
    onNewMessage: handleNewMessage,
    onUserTyping: () => {},
    onMessageRead: () => {},
    onNotification: handleNotification,
  });

  // Auto-scroll
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages]);

  // Mark read
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      const last = messages[messages.length - 1];
      if (last && last.sender_id !== user?.id) markAsRead(selectedConversation, last.id);
    }
  }, [selectedConversation, messages, user?.id, markAsRead]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const startPeerConvMutation = useMutation({
    mutationFn: (otherProfId: string) => chatAPI.getOrCreatePeerConversation(otherProfId),
    onSuccess: (conv) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      setSelectedConversation(conv.id);
      setActiveTab('chats');
    },
    onError: () => toast.error(t('chat.startConversationError')),
  });

  const sendMsgMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!selectedConversation) throw new Error(t('chat.noConversationSelected'));
      if (socketOk) { sendViaSocket(selectedConversation, content); return null; }
      return chatAPI.sendMessage(selectedConversation, content);
    },
    onSuccess: () => {
      setNewMessage('');
      stopTyping(selectedConversation || '');
      queryClient.invalidateQueries({ queryKey: ['messages', selectedConversation] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('chat.sendError')),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleInputChange = (value: string) => {
    setNewMessage(value);
    if (selectedConversation) {
      if (value.trim()) {
        startTyping(selectedConversation);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => stopTyping(selectedConversation), 3000);
      } else {
        stopTyping(selectedConversation);
      }
    }
  };

  const handleSend = () => {
    if (!newMessage.trim() || !selectedConversation) return;
    sendMsgMutation.mutate(newMessage.trim());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const insertEmoji = (emoji: string) => {
    const ta = textareaRef.current;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const next = newMessage.slice(0, start) + emoji + newMessage.slice(end);
      setNewMessage(next);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + emoji.length; ta.focus(); }, 0);
    } else {
      setNewMessage(prev => prev + emoji);
    }
    setShowEmojiPicker(false);
  };

  // ── Filtered lists ───────────────────────────────────────────────────────

  const peerAndGroupConvs = conversations.filter(c =>
    c.type === 'professional_peer' || c.type === 'professional_group'
  );

  const filteredConvs = peerAndGroupConvs.filter(c => {
    const term = chatSearch.toLowerCase();
    if (!term) return true;
    const name = c.group_name
      || (c.participant ? `${c.participant.first_name} ${c.participant.last_name}` : '');
    return name.toLowerCase().includes(term) || c.last_message?.toLowerCase().includes(term);
  });

  const filteredDirectConvs = filteredConvs.filter(c => c.type === 'professional_peer');
  const filteredGroupConvs = filteredConvs.filter(c => c.type === 'professional_group');

  const selectedConvData = conversations.find(c => c.id === selectedConversation);

  const convDisplayName = selectedConvData
    ? selectedConvData.type === 'professional_group'
      ? selectedConvData.group_name || t('chat.group')
      : selectedConvData.participant
        ? `${selectedConvData.participant.first_name} ${selectedConvData.participant.last_name}`
        : selectedConvData.onderwerp
    : '';

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" style={{ height: 'calc(100vh - 220px)' }}>

        {/* ── Left panel: directory + chats ── */}
        <div className="lg:col-span-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">

          {/* Tab bar */}
          <div className="flex border-b">
            {([
              { id: 'directory', label: t('chat.tabProfessionals'), icon: Users },
              { id: 'chats', label: t('chat.tabChats'), icon: MessageCircle },
            ] as { id: TabId; label: string; icon: React.FC<any> }[]).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'chats' && filteredConvs.length > 0 && (
                  <span className="ml-1 bg-blue-100 text-blue-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                    {filteredConvs.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {activeTab === 'directory' && (
            <>
              {/* Directory search + filters */}
              <div className="p-3 space-y-2 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('chat.searchByName')}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={dirSearch}
                    onChange={e => setDirSearch(e.target.value)}
                  />
                </div>
                <div className="flex gap-2">
                  {/* Province filter */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => { setShowProvinceFilter(v => !v); setShowTypeFilter(false); }}
                      className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs border rounded-lg ${filterProvince ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      <span>{filterProvince || t('chat.filterProvince')}</span>
                      <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />
                    </button>
                    {showProvinceFilter && (
                      <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        <button
                          onClick={() => { setFilterProvince(''); setShowProvinceFilter(false); }}
                          className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:bg-gray-50"
                        >
                          {t('chat.allProvinces')}
                        </button>
                        {PROVINCES.map(p => (
                          <button
                            key={p}
                            onClick={() => { setFilterProvince(p); setShowProvinceFilter(false); }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 ${filterProvince === p ? 'text-blue-700 font-medium' : 'text-gray-700'}`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Type filter */}
                  <div className="relative flex-1">
                    <button
                      onClick={() => { setShowTypeFilter(v => !v); setShowProvinceFilter(false); }}
                      className={`w-full flex items-center justify-between px-2.5 py-1.5 text-xs border rounded-lg ${filterType ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      <span className="truncate">{filterType ? PROFESSIONAL_TYPES.find(pt => pt.value === filterType)?.label : t('chat.filterRole')}</span>
                      <ChevronDown className="w-3 h-3 ml-1 flex-shrink-0" />
                    </button>
                    {showTypeFilter && (
                      <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        <button
                          onClick={() => { setFilterType(''); setShowTypeFilter(false); }}
                          className="w-full text-left px-3 py-2 text-xs text-gray-500 hover:bg-gray-50"
                        >
                          {t('chat.allRoles')}
                        </button>
                        {PROFESSIONAL_TYPES.map(t => (
                          <button
                            key={t.value}
                            onClick={() => { setFilterType(t.value); setShowTypeFilter(false); }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-50 ${filterType === t.value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Active filter chips */}
                {(filterProvince || filterType) && (
                  <div className="flex flex-wrap gap-1">
                    {filterProvince && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                        {filterProvince}
                        <button onClick={() => setFilterProvince('')}><X className="w-3 h-3" /></button>
                      </span>
                    )}
                    {filterType && (
                      <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">
                        {PROFESSIONAL_TYPES.find(t => t.value === filterType)?.label}
                        <button onClick={() => setFilterType('')}><X className="w-3 h-3" /></button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Directory list */}
              <div className="flex-1 overflow-y-auto divide-y">
                {dirLoading ? (
                  <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                ) : professionals.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <Users className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">{t('chat.noProfessionalsFound')}</p>
                  </div>
                ) : (
                  professionals.map(p => (
                    <div key={p.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                      <Avatar photoPath={p.profile_photo_path} firstName={p.first_name} lastName={p.last_name} size="sm" isOnline={p.is_online} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.first_name} {p.last_name}</p>
                        <p className="text-xs text-gray-500 truncate">{p.professional_type_label}</p>
                        {p.active_regions && Array.isArray(p.active_regions) && p.active_regions.length > 0 && (
                          <p className="text-xs text-gray-400 truncate">{(p.active_regions as string[]).slice(0, 2).join(', ')}</p>
                        )}
                      </div>
                      <button
                        onClick={() => startPeerConvMutation.mutate(p.id)}
                        disabled={startPeerConvMutation.isPending}
                        title={t('chat.sendMessage')}
                        className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors flex-shrink-0"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {activeTab === 'chats' && (
            <>
              {/* Chats header with new group button */}
              <div className="p-3 border-b flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('chat.searchConversations')}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={chatSearch}
                    onChange={e => setChatSearch(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setShowGroupModal(true)}
                  title={t('chat.newGroupTitle')}
                  className="flex-shrink-0 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {/* Conversations list */}
              <div className="flex-1 overflow-y-auto">
                {convsLoading ? (
                  <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                ) : filteredConvs.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <MessageCircle className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500">{t('chat.noConversationsYet')}</p>
                    <p className="text-xs text-gray-400 mt-1">{t('chat.noConversationsHint')}</p>
                  </div>
                ) : (
                  <>
                    {/* Directe berichten */}
                    <div className="px-3 pt-3 pb-1">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <MessageCircle className="w-3.5 h-3.5" />
                        Directe berichten
                        {filteredDirectConvs.length > 0 && (
                          <span className="ml-auto text-gray-300 font-normal normal-case tracking-normal">{filteredDirectConvs.length}</span>
                        )}
                      </p>
                    </div>
                    <div className="divide-y">
                      {filteredDirectConvs.length === 0 ? (
                        <p className="px-4 py-3 text-xs text-gray-400 italic">{t('chat.noConversationsHint')}</p>
                      ) : filteredDirectConvs.map(conv => {
                        const displayName = conv.participant
                          ? `${conv.participant.first_name} ${conv.participant.last_name}`
                          : conv.onderwerp;
                        return (
                          <div
                            key={conv.id}
                            onClick={() => setSelectedConversation(conv.id)}
                            className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedConversation === conv.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <Avatar
                                photoPath={conv.participant?.profile_photo}
                                firstName={conv.participant?.first_name}
                                lastName={conv.participant?.last_name}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                                  {conv.unread_count && conv.unread_count > 0 ? (
                                    <span className="flex-shrink-0 ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                      {conv.unread_count}
                                    </span>
                                  ) : null}
                                </div>
                                <p className="text-xs text-gray-500 truncate mt-0.5">{conv.last_message || t('chat.noMessages')}</p>
                                {conv.last_message_at && (
                                  <div className="flex items-center mt-0.5 text-xs text-gray-400">
                                    <Clock className="w-3 h-3 mr-0.5" />
                                    {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true, locale: nl })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Groepen */}
                    <div className="px-3 pt-4 pb-1 border-t border-gray-100 mt-1">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        Groepen
                        {filteredGroupConvs.length > 0 && (
                          <span className="ml-auto text-gray-300 font-normal normal-case tracking-normal">{filteredGroupConvs.length}</span>
                        )}
                      </p>
                    </div>
                    <div className="divide-y">
                      {filteredGroupConvs.length === 0 ? (
                        <p className="px-4 py-3 text-xs text-gray-400 italic">{t('chat.noConversationsHint')}</p>
                      ) : filteredGroupConvs.map(conv => {
                        const displayName = conv.group_name || t('chat.group');
                        return (
                          <div
                            key={conv.id}
                            onClick={() => setSelectedConversation(conv.id)}
                            className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedConversation === conv.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                <Users className="w-5 h-5 text-purple-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                                  {conv.unread_count && conv.unread_count > 0 ? (
                                    <span className="flex-shrink-0 ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                      {conv.unread_count}
                                    </span>
                                  ) : null}
                                </div>
                                <p className="text-xs text-gray-500 truncate mt-0.5">{conv.last_message || t('chat.noMessages')}</p>
                                {conv.last_message_at && (
                                  <div className="flex items-center mt-0.5 text-xs text-gray-400">
                                    <Clock className="w-3 h-3 mr-0.5" />
                                    {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true, locale: nl })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Right panel: messages ── */}
        <div className="lg:col-span-2 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {selectedConversation && selectedConvData ? (
            <>
              {/* Chat header */}
              <div className="flex items-center gap-3 p-4 border-b bg-white">
                {selectedConvData.type === 'professional_group' ? (
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                ) : (
                  <Avatar
                    photoPath={selectedConvData.participant?.profile_photo}
                    firstName={selectedConvData.participant?.first_name}
                    lastName={selectedConvData.participant?.last_name}
                  />
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{convDisplayName}</h3>
                  {typingUsers.length > 0 ? (
                    <p className="text-xs text-gray-400 italic">{t('chat.typing')}</p>
                  ) : selectedConvData.type === 'professional_group' ? (
                    <p className="text-xs text-gray-400">{t('chat.groupConversation')}</p>
                  ) : null}
                </div>
                {selectedConvData.type !== 'professional_group' && <BelKnop conversationId={selectedConvData.id} />}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {msgsLoading ? (
                  <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-12">
                    <MessageCircle className="h-14 w-14 text-gray-200" />
                    <p className="mt-3 text-sm font-medium text-gray-600">{t('chat.noMessagesYet')}</p>
                    <p className="text-xs text-gray-400 mt-1">{t('chat.sendFirst')}</p>
                  </div>
                ) : (
                  <>
                    {messages.map(msg => {
                      const isOwn = msg.sender_id === user?.id;
                      return (
                        <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-xs lg:max-w-md xl:max-w-lg px-4 py-2.5 rounded-2xl shadow-sm ${
                            isOwn ? 'bg-blue-500 text-white rounded-br-md' : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          }`}>
                            {!isOwn && msg.first_name && (
                              <p className="text-xs font-semibold mb-1 opacity-70">{msg.first_name} {msg.last_name}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                            <p className={`text-xs mt-1 text-right ${isOwn ? 'text-blue-100' : 'text-gray-400'}`}>
                              {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true, locale: nl })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Message input */}
              <div className="p-3 border-t bg-gray-50">
                <div className="flex items-end gap-2 bg-white border border-gray-300 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
                  {/* Emoji button */}
                  <div className="relative">
                    <button
                      ref={emojiButtonRef}
                      type="button"
                      onClick={() => setShowEmojiPicker(v => !v)}
                      className="p-1 text-gray-400 hover:text-yellow-500 transition-colors"
                      title={t('chat.insertEmoji')}
                    >
                      <Smile className="w-5 h-5" />
                    </button>
                    {showEmojiPicker && (
                      <div className="absolute bottom-10 left-0 z-30">
                        <EmojiPicker onSelect={insertEmoji} onClose={() => setShowEmojiPicker(false)} />
                      </div>
                    )}
                  </div>

                  <textarea
                    ref={textareaRef}
                    rows={1}
                    placeholder={t('chat.typeMessage')}
                    className="flex-1 resize-none bg-transparent text-sm focus:outline-none py-1 max-h-32"
                    value={newMessage}
                    onChange={e => handleInputChange(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sendMsgMutation.isPending}
                  />

                  <button
                    onClick={handleSend}
                    disabled={!newMessage.trim() || sendMsgMutation.isPending}
                    className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  >
                    {sendMsgMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5 ml-1">{t('chat.sendHint')}</p>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                <MessageCircle className="w-10 h-10 text-blue-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700">{t('chat.selectConversation')}</h3>
              <p className="text-sm text-gray-400 mt-1 max-w-xs">
                {t('chat.selectConversationHint')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Group creation modal */}
      {showGroupModal && user && (
        <CreateGroupModal
          onClose={() => setShowGroupModal(false)}
          onCreated={(conv) => {
            queryClient.invalidateQueries({ queryKey: ['conversations'] });
            setSelectedConversation(conv.id);
            setActiveTab('chats');
            setShowGroupModal(false);
          }}
          currentUserId={user.id}
        />
      )}
    </div>
  );
};

export default ProfessionalChatPage;
